/* eslint-disable no-nested-ternary */

import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';

import {
  Close,
  Delete,
  OpenInNew,
  UploadFile,
} from '@mui/icons-material';
import PushPinIcon from '@mui/icons-material/PushPin';
import Star from '@mui/icons-material/Star';
import Visibility from '@mui/icons-material/Visibility';
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Slide,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { patchEvent } from '@pingum/app-state-management/event/actions';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import ImportanceSelect from '../../../../../../../components/EnumSelects/ImportanceSelect';
import NoteTypeSelect from '../../../../../../../components/EnumSelects/NoteTypeSelect';
import Horse from '../../../../../../../components/Horse';
import Property from '../../../../../../../components/Property';
import ReactFileUpload from '../../../../../../../components/ReactFileUpload';
import { setGlobalModals } from '../../../../../../../redux/app/actions';
import { TASK_MODAL_TYPE } from '../../../../../../../redux/app/types';
import {
  addHorseFile,
  addHorseNote,
  deleteHorseNote,
  getHorseNoteHighlights,
  updateHorseNote,
} from '../../../../../../../redux/horse/actions';
import { primary } from '../../../../../../../utils/brandColors';
import {
  fromPickerValue,
  toMomentValue,
} from '../../../../../../../utils/dateHelper';
import theme from '../../../../../../../utils/theme';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const AddEditModal = ({
  open,
  defaultHighlight,
  onClose,
  horseId,
  pinnedHorseNoteId,
  horseNote,
  presetType,
  taskId,
  task,
}) => {
  // eslint-disable-next-line no-unused-vars
  const dispatch = useDispatch();
  const fullscreen = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useColorScheme();

  // React state
  const [pinToHorse, setPinToHorse] = React.useState(false);
  const [highlightOrder, setHighlightOrder] = React.useState(null);
  const [type, setType] = React.useState('Note');
  const [importance, setImportance] = React.useState('Inform');
  const [note, setNote] = React.useState('');
  const [noteValidation, setNoteValidation] = React.useState(null);
  const [title, setTitle] = React.useState('');
  const [titleValidation, setTitleValidation] = React.useState(null);
  const [noteDate, setNoteDate] = React.useState(null);
  const [files, setFiles] = React.useState([]);
  const [clientAccess, setClientAccess] = React.useState(false);
  const [information, setInformation] = React.useState({});
  const [savingError, setSavingError] = React.useState(null);
  const [filesToRemove, setFilesToRemove] = React.useState([]);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  // const [loadNoteFailure, setLoadNoteFailure] = React.useState(false);

  // Redux state
  const isAddingHorseNotes = useSelector((state) => state.horse.isAddingHorseNotes);
  const isUpdatingHorseNotes = useSelector((state) => state.horse.isUpdatingHorseNotes);
  const isAddingHorseFile = useSelector((state) => state.horse.isAddingHorseFile);
  const horseNoteHighlights = useSelector((state) => state.horse.horseNoteHighlights);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const eventData = useSelector((state) => state.eventData.eventData);
  const isLoading = isAddingHorseNotes || isUpdatingHorseNotes || isAddingHorseFile;

  // const loadNote = (retry, taskId) => {
  //   if (taskId) {
  //     dispatch(getNoteForTask(horseId, taskId)).then((taskHorseNote) => {
  //       if ((!taskHorseNote || !taskHorseNote.id) && retry < 4) {
  //         // retry again in a few seconds
  //         setTimeout(() => {
  //           loadNote(retry + 1, taskId);
  //         }, 3000);
  //       } else {
  //         setLoadNoteFailure(true);
  //       }
  //     }).catch((error) => {
  //       console.error(error);
  //       setLoadNoteFailure(true);
  //       // Do nothing?
  //     });
  //   }
  // };

  React.useEffect(() => {
    if (open && horseId) {
      // load horse highlights so we can know next order if neededs
      dispatch(getHorseNoteHighlights(horseId, true));
      // setLoadNoteFailure(false)
    }
    setConfirmDelete(false);
    setFiles([]);
    setFilesToRemove([]);
    setPinToHorse((pinnedHorseNoteId && horseNote)
      ? pinnedHorseNoteId === horseNote.id
      : false);
    setHighlightOrder((horseNote)
      ? horseNote.highlightOrder
      : (defaultHighlight && 100) || null);
    if (!horseNote) {
      setType(presetType || 'Note');
    } else {
      setType(horseNote.type);
    }
    setNote(horseNote
      ? horseNote.note
      : '');
    setNoteValidation(null);
    setTitle(horseNote
      ? horseNote.title
      : '');
    setTitleValidation(null);
    setImportance(horseNote
      ? horseNote.importance
      : 'Inform');
    setClientAccess(horseNote
      ? horseNote.clientAccess
      : false);
    setInformation(horseNote
      ? horseNote.information
      : {});
    setNoteDate(horseNote
      ? new Date(horseNote.createdTimestamp)
      : null)
  }, [horseNote, open]);

  const getEDNameFromId = (id) => (eventData.find((ed) => ed.id === id) || { name: 'Unknown' }).name;

  const validateFields = () => {
    if (!title || title.length < 2 || title.length > 150) {
      setTitleValidation('Please provide a title between 2 and 150 characters in length.');
      return false;
    } setTitleValidation(null);
    if (note.length > 2048) {
      setNoteValidation('Note cannot exceed 2048 characters in length..');
      return false;
    } setNoteValidation(null);
    return true;
  };

  const handleUpdateTask = (newDescription, newImportance, newType, removeNote = false) => {
    if (task && task.id) {
      const newEventData = task.eventData;
      if (removeNote) {
        delete newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteEventDataId)];
        delete newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteImportanceEventDataId)];
        delete newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)];
      } else {
        newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteEventDataId)] = {
          type: 'Text',
          value: `${newDescription}`,
        };
        newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteImportanceEventDataId)] = {
          type: 'Text',
          value: `${newImportance}`,
        };
        newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)] = {
          type: 'Text',
          value: `${newType}`,
        };
      }
      dispatch(patchEvent(task.id, { eventData: newEventData }));
      onClose(true);
    } else {
      onClose(false);
    }
  };

  const addFilesAndPatchNote = (newHorseNote) => {
    const allRequests = [];
    for (let i = 0; i < files.length; i += 1) {
      allRequests.push(dispatch(addHorseFile(horseId, newHorseNote.id, files[i], clientAccess)));
    }

    // once all done, process
    Promise.all(allRequests).then((values) => {
      // eslint-disable-next-line no-console

      dispatch(updateHorseNote(
        horseId,
        newHorseNote.id,
        { information: { files: [...((newHorseNote.information && newHorseNote.information.files) || []), ...values] } },
      )).then(() => {
        handleUpdateTask(note, importance, type, false);
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the horse note. Please contact Questri support.');
        console.error(error);
      });
    });
  };

  const handleDelete = () => {
    dispatch(deleteHorseNote(horseId, horseNote.id)).then(() => {
      setConfirmDelete(false);
      handleUpdateTask(null, null, null, true);
      onClose();
    }).catch(() => {
      setConfirmDelete(false);
      setSavingError('There was an error deleting this note. Please contact support');
    });
  };

  const handleSave = () => {
    const isNewHorseNote = !horseNote;
    if (!validateFields()) { return; }

    const request = {
      setAsHorseActiveNote: pinToHorse,
      type,
      importance,
      note,
      title,
      clientAccess,
      information,
      noteDate,
      taskId,
      // If editing horse note and it has order, keep that order
      horseHighlight: (highlightOrder && !isNewHorseNote)
        ? horseNote.highlightOrder
        : highlightOrder
          ? horseNoteHighlights.length > 0
            ? (Math.max(...horseNoteHighlights.map((o) => o.highlightOrder)) + 1)
            : 1
          : null,
    };

    // Adding new location
    if (isNewHorseNote) {
      dispatch(addHorseNote(horseId, request)).then((newHorseNote) => {
        if (files.length > 0) {
          // upload files then patch note with files
          addFilesAndPatchNote(newHorseNote);
        } else {
          handleUpdateTask(note, importance, type, false);
        }
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to add this note to the horse. Please contact Questri support.');
        console.error(error);
      });
    } else {
      if (filesToRemove && filesToRemove.length > 0 && information.files && information.files.length > 0) {
        // remove any removed files
        const updatedFilesList = information.files.filter((f) => filesToRemove.indexOf(f) < 0);
        request.information = {
          ...request.information,
          files: updatedFilesList,
        };
      }
      dispatch(updateHorseNote(horseId, horseNote.id, {
        ...request,
        removeHorseHighlightOrder: !highlightOrder,
      })).then((updatedHorseNote) => {
        if (files.length > 0) {
          // upload files then patch note with files
          addFilesAndPatchNote(updatedHorseNote);
        } else {
          handleUpdateTask(note, importance, type, false);
        }
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the horse note. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const horseRecord = (horseId && barnHorsesMap[horseId] && barnHorsesMap[horseId][0]) || null;
  const modalTitle = horseNote
    ? 'Edit Horse Note'
    : 'Add Horse Note';
  const modalSubtitle = horseNote
    ? 'Update details and keep the barn aligned.'
    : 'Capture important context so everyone stays informed.';

  return (

    <Dialog
      TransitionComponent={Transition}
      open={open}
      fullScreen={fullscreen}
      onClose={onClose}
      PaperProps={{
        sx: {
          m: 0,
          mx: fullscreen
            ? 0
            : 2,
          width: '100%',
          maxWidth: 900,
        },
        onClick: (e) => e.stopPropagation(),
      }}
      aria-labelledby="modal-add-horse-note"
      aria-describedby="modal-add-horse-note-description"
    >
      <DialogTitle
        sx={{
          background: mode === 'light'
            ? primary.main
            : 'var(--template-palette-background-paper)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            id="modal-add-horse-title"
            variant="h4"
            component="span"
            color="#fff"
          >
            {modalTitle} {horseNote && `(${horseNote.type})`}
          </Typography>
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 300,
            }}
            variant="body2"
          >{modalSubtitle}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><Close sx={{ color: '#fff' }} /></IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ p: 3 }}
      >
        {horseRecord && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mt: 3,
            }}
          >
            <Horse horse={horseRecord.horse} />

            {horseNote && horseNote.taskId && (
              <Box>
                <Property label="Associated Task">
                  <Button
                    endIcon={<OpenInNew />}
                    sx={{ p: 0 }}
                    size="small"
                    onClick={(e) => {
                      dispatch(setGlobalModals(TASK_MODAL_TYPE, { id: horseNote.taskId }, null, null, {}, null));
                      e.stopPropagation();
                      onClose();
                    }}
                  >
                    Open Task
                  </Button>
                </Property>
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            py: 3,
          }}
        >
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 8,
                }}
              >
                <TextField
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); }}
                  fullWidth
                  disabled={isLoading}
                  label="Headline"
                  error={!!titleValidation}
                  helperText={titleValidation}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Date"
                    value={toMomentValue(noteDate || new Date())}
                    disabled={isLoading}
                    onChange={(newValue) => {
                      setNoteDate(fromPickerValue(newValue));
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <NoteTypeSelect value={type} onChange={(e) => { setType(e.target.value); }} />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <ImportanceSelect value={importance} onChange={(e) => { setImportance(e.target.value); }} />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={note}
                  onChange={(e) => { setNote(e.target.value); }}
                  fullWidth
                  disabled={isLoading}
                  label="Note Details"
                  multiline
                  minRows={3}
                  error={!!noteValidation}
                  helperText={noteValidation}
                />
              </Grid>
              <Grid size={12}>
                <ReactFileUpload
                  value={files}
                  onChange={setFiles}
                  title="Add files to this note"
                  disabled={isLoading}
                  dense
                  buttonProps={{
                    size: 'small',
                    variant: 'outlined',
                  }}
                />
              </Grid>
              {horseNote && horseNote.information && horseNote.information.files
                && horseNote.information.files.filter((file) => filesToRemove.indexOf(file) < 0).length > 0
                && (
                  <Grid size={12}>
                    <Box
                      sx={(themee) => ({
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${themee.palette.divider}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      })}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Current Attachments
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        {horseNote.information.files
                          .filter((file) => filesToRemove.indexOf(file) < 0)
                          .map((file) => (
                            <Chip
                              key={`delete-file-${file}`}
                              label={file}
                              onDelete={() => { setFilesToRemove([...filesToRemove, file]); }}
                              icon={<UploadFile />}
                              variant="outlined"
                              sx={{ maxWidth: 200 }}
                              deleteIcon={<Delete />}
                            />
                          ))}
                      </Box>
                    </Box>
                  </Grid>
                )}
            </Grid>

            <Divider />

            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Box
                  sx={(themee) => ({
                    borderRadius: 2,
                    border: `1px solid ${themee.palette.divider}`,
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                  })}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={(
                      <Checkbox
                        checked={clientAccess}
                        onChange={(e) => { setClientAccess(e.target.checked); }}
                        disabled={isLoading}
                      />
                    )}
                    label={(
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      ><Typography>Share with client</Typography><Visibility fontSize="small" color="primary" />
                      </Box>
                    )}
                  />
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Box
                  sx={(themee) => ({
                    borderRadius: 2,
                    border: `1px solid ${themee.palette.divider}`,
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                  })}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={(
                      <Checkbox
                        checked={pinToHorse}
                        onChange={(e) => { setPinToHorse(e.target.checked); }}
                        disabled={isLoading}
                      />
                    )}
                    label={(
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      ><Typography>Pin to Horse</Typography><PushPinIcon fontSize="small" color="primary" />
                      </Box>
                    )}
                  />
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Box
                  sx={(themee) => ({
                    borderRadius: 2,
                    border: `1px solid ${themee.palette.divider}`,
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                  })}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={(
                      <Checkbox
                        checked={!!highlightOrder}
                        onChange={(e) => {
                          setHighlightOrder(e.target.checked
                            ? 100
                            : null);
                        }}
                        disabled={isLoading}
                      />
                    )}
                    label={(
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      ><Typography>Horse Highlight</Typography><Star fontSize="small" color="primary" />
                      </Box>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Box>
        <Divider
          sx={{ my: 2 }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >{horseNote
        ? (
          <Button
            onClick={() => {
              if (confirmDelete) {
                handleDelete();
              } else {
                setConfirmDelete(true);
              }
            }}
            disabled={isLoading}
            variant="outlined"
            color="error"
          >
            {confirmDelete
              ? 'Confirm Delete'
              : 'Delete'}
          </Button>
        )
        : <Box />}
        {savingError && (
          <Typography variant="caption" component="div" color="error" gutterBottom>{savingError}</Typography>
        )}
        <Button onClick={handleSave} disabled={isLoading} variant="contained" color="primary" startIcon={<SaveIcon />}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

AddEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  defaultHighlight: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  horseId: PropTypes.string,
  pinnedHorseNoteId: PropTypes.string,
  horseNote: PropTypes.object,
  presetType: PropTypes.string,
  taskId: PropTypes.number,
  task: PropTypes.object,
};

AddEditModal.defaultProps = {
  defaultHighlight: false,
  horseNote: null,
  pinnedHorseNoteId: null,
  presetType: null,
  taskId: null,
  task: null,
  horseId: null,
};

export default AddEditModal;
